import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  UserPlus,
  Crown,
  Edit3,
  Eye,
  Trash2,
  Copy,
  Check,
  Link2,
  Globe,
  Lock,
  Mail,
  Loader2,
  X,
  MoreHorizontal,
  Shield,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { documentsApi } from '../lib/api';

interface Collaborator {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  permissionLevel: 'owner' | 'editor' | 'viewer';
  addedAt: string;
  isOnline?: boolean;
}

interface ShareSettings {
  isPublic: boolean;
  publicPermission: 'viewer' | 'editor';
  shareLink?: string;
}

interface CollaboratorsListProps {
  documentId: string;
  currentUserId?: string;
  isOwner: boolean;
  onClose?: () => void;
}

export default function CollaboratorsList({
  documentId,
  currentUserId,
  isOwner,
  onClose,
}: CollaboratorsListProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('ar') ? 'ar' : 'en';
  
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareSettings, setShareSettings] = useState<ShareSettings>({
    isPublic: false,
    publicPermission: 'viewer',
  });
  
  // Invite states
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState<'editor' | 'viewer'>('viewer');
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [inviteMessage, setInviteMessage] = useState('');
  
  // Link states
  const [linkCopied, setLinkCopied] = useState(false);
  const [showMenu, setShowMenu] = useState<string | null>(null);

  // Load collaborators
  useEffect(() => {
    const fetchCollaborators = async () => {
      setLoading(true);
      try {
        // In a real app, fetch from API
        // For now, use mock data
        setCollaborators([
          {
            id: currentUserId || '1',
            email: 'you@example.com',
            name: lang === 'ar' ? 'أنت (المالك)' : 'You (Owner)',
            permissionLevel: 'owner',
            addedAt: new Date().toISOString(),
            isOnline: true,
          },
        ]);
        
        setShareSettings({
          isPublic: false,
          publicPermission: 'viewer',
          shareLink: `${window.location.origin}/app/document/${documentId}`,
        });
      } catch (error) {
        console.error('Failed to fetch collaborators:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCollaborators();
  }, [documentId, currentUserId, lang]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    
    setInviteStatus('sending');
    setInviteMessage('');
    
    try {
      await documentsApi.share(documentId, inviteEmail.trim(), invitePermission);
      
      // Add to local list
      setCollaborators(prev => [
        ...prev,
        {
          id: crypto.randomUUID(),
          email: inviteEmail.trim(),
          name: inviteEmail.trim().split('@')[0],
          permissionLevel: invitePermission,
          addedAt: new Date().toISOString(),
        },
      ]);
      
      setInviteStatus('success');
      setInviteMessage(lang === 'ar' ? 'تم إرسال الدعوة' : 'Invitation sent');
      setInviteEmail('');
      
      setTimeout(() => setInviteStatus('idle'), 2000);
    } catch (error) {
      setInviteStatus('error');
      setInviteMessage(lang === 'ar' ? 'فشل في الإرسال' : 'Failed to send invite');
    }
  };

  const handleCopyLink = () => {
    if (shareSettings.shareLink) {
      navigator.clipboard.writeText(shareSettings.shareLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleRemoveCollaborator = async (collaboratorId: string) => {
    // In a real app, call API to remove
    setCollaborators(prev => prev.filter(c => c.id !== collaboratorId));
    setShowMenu(null);
  };

  const handleChangePermission = async (collaboratorId: string, newPermission: 'editor' | 'viewer') => {
    setCollaborators(prev =>
      prev.map(c =>
        c.id === collaboratorId ? { ...c, permissionLevel: newPermission } : c
      )
    );
    setShowMenu(null);
  };

  const togglePublicAccess = () => {
    setShareSettings(prev => ({
      ...prev,
      isPublic: !prev.isPublic,
    }));
  };

  const getPermissionIcon = (level: string) => {
    switch (level) {
      case 'owner':
        return <Crown className="h-4 w-4 text-amber-500" />;
      case 'editor':
        return <Edit3 className="h-4 w-4 text-blue-500" />;
      case 'viewer':
        return <Eye className="h-4 w-4 text-green-500" />;
      default:
        return null;
    }
  };

  const getPermissionLabel = (level: string) => {
    const labels = {
      owner: lang === 'ar' ? 'مالك' : 'Owner',
      editor: lang === 'ar' ? 'محرر' : 'Editor',
      viewer: lang === 'ar' ? 'مشاهد' : 'Viewer',
    };
    return labels[level as keyof typeof labels] || level;
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">
            {lang === 'ar' ? 'إدارة التعاون' : 'Collaboration'}
          </h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn-icon">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Public Access Toggle */}
      <div className="mb-4 p-3 rounded-lg border bg-secondary/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {shareSettings.isPublic ? (
              <Globe className="h-5 w-5 text-green-500" />
            ) : (
              <Lock className="h-5 w-5 text-muted-foreground" />
            )}
            <div>
              <p className="text-sm font-medium">
                {shareSettings.isPublic
                  ? (lang === 'ar' ? 'رابط عام' : 'Public link enabled')
                  : (lang === 'ar' ? 'خاص' : 'Private')}
              </p>
              <p className="text-xs text-muted-foreground">
                {shareSettings.isPublic
                  ? (lang === 'ar' ? 'أي شخص لديه الرابط يمكنه العرض' : 'Anyone with link can view')
                  : (lang === 'ar' ? 'المدعوون فقط' : 'Only invited people')}
              </p>
            </div>
          </div>
          {isOwner && (
            <button
              onClick={togglePublicAccess}
              className={cn(
                'relative w-11 h-6 rounded-full transition-colors',
                shareSettings.isPublic ? 'bg-green-500' : 'bg-muted'
              )}
            >
              <span
                className={cn(
                  'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-sm',
                  shareSettings.isPublic ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          )}
        </div>
        
        {/* Share Link */}
        <div className="mt-3 flex gap-2">
          <div className="flex-1 relative">
            <Link2 className="absolute start-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={shareSettings.shareLink || ''}
              readOnly
              className="input ps-9 text-xs h-9 bg-background"
            />
          </div>
          <button
            onClick={handleCopyLink}
            className="btn-secondary h-9 px-3 gap-1.5"
          >
            {linkCopied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            <span className="text-xs">
              {linkCopied ? (lang === 'ar' ? 'تم' : 'Copied') : (lang === 'ar' ? 'نسخ' : 'Copy')}
            </span>
          </button>
        </div>
      </div>

      {/* Invite Section */}
      {isOwner && (
        <div className="mb-4 p-3 rounded-lg border">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">
              {lang === 'ar' ? 'دعوة مستخدم' : 'Invite people'}
            </span>
          </div>
          
          <div className="flex gap-2 mb-2">
            <div className="flex-1 relative">
              <Mail className="absolute start-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                placeholder={lang === 'ar' ? 'البريد الإلكتروني' : 'Email address'}
                className="input ps-9 h-9 text-sm"
              />
            </div>
            <select
              value={invitePermission}
              onChange={(e) => setInvitePermission(e.target.value as 'editor' | 'viewer')}
              className="input h-9 w-28 text-sm"
            >
              <option value="viewer">{lang === 'ar' ? 'مشاهد' : 'Viewer'}</option>
              <option value="editor">{lang === 'ar' ? 'محرر' : 'Editor'}</option>
            </select>
          </div>
          
          <button
            onClick={handleInvite}
            disabled={inviteStatus === 'sending' || !inviteEmail.trim()}
            className="btn-primary w-full h-9 text-sm"
          >
            {inviteStatus === 'sending' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              lang === 'ar' ? 'إرسال الدعوة' : 'Send invite'
            )}
          </button>
          
          {inviteMessage && (
            <p className={cn(
              'mt-2 text-xs text-center',
              inviteStatus === 'success' ? 'text-green-600' : 'text-destructive'
            )}>
              {inviteMessage}
            </p>
          )}
        </div>
      )}

      {/* Collaborators List */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">
            {lang === 'ar' ? 'المتعاونون' : 'People with access'}
          </span>
          <span className="text-xs text-muted-foreground">
            {collaborators.length} {lang === 'ar' ? 'عضو' : 'member(s)'}
          </span>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-1">
            {collaborators.map((collaborator) => (
              <div
                key={collaborator.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 group"
              >
                {/* Avatar */}
                <div className="relative">
                  <div
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center text-sm font-medium"
                    style={{
                      backgroundColor: collaborator.avatarUrl
                        ? undefined
                        : `hsl(${collaborator.email.charCodeAt(0) * 10}, 70%, 50%)`,
                    }}
                  >
                    {collaborator.avatarUrl ? (
                      <img
                        src={collaborator.avatarUrl}
                        alt={collaborator.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-white">
                        {collaborator.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  {collaborator.isOnline && (
                    <span className="absolute -bottom-0.5 -end-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-background" />
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{collaborator.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{collaborator.email}</p>
                </div>
                
                {/* Permission Badge */}
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-secondary/50 text-xs">
                  {getPermissionIcon(collaborator.permissionLevel)}
                  <span>{getPermissionLabel(collaborator.permissionLevel)}</span>
                </div>
                
                {/* Actions Menu */}
                {isOwner && collaborator.permissionLevel !== 'owner' && (
                  <div className="relative">
                    <button
                      onClick={() => setShowMenu(showMenu === collaborator.id ? null : collaborator.id)}
                      className="btn-icon opacity-0 group-hover:opacity-100"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                    
                    {showMenu === collaborator.id && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setShowMenu(null)}
                        />
                        <div className="absolute end-0 top-full mt-1 z-50 w-40 rounded-lg border bg-popover shadow-lg py-1">
                          <button
                            onClick={() => handleChangePermission(
                              collaborator.id,
                              collaborator.permissionLevel === 'editor' ? 'viewer' : 'editor'
                            )}
                            className="menu-item text-sm"
                          >
                            <Shield className="h-4 w-4" />
                            {collaborator.permissionLevel === 'editor'
                              ? (lang === 'ar' ? 'تعيين كمشاهد' : 'Make viewer')
                              : (lang === 'ar' ? 'تعيين كمحرر' : 'Make editor')}
                          </button>
                          <button
                            onClick={() => handleRemoveCollaborator(collaborator.id)}
                            className="menu-item text-sm text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            {lang === 'ar' ? 'إزالة' : 'Remove'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
